/**
 * Tax Documents Page
 *
 * Displays guide tax document access (1099-K, 1099-NEC) and W-9 status.
 * All tax document generation is handled by Stripe; we surface status and links.
 *
 * Epic: MVP 1.21.6 - Revenue Dashboard & Stripe-Dependent Features
 * Story: SCRUM-544 - Tax Document Access
 * Story: SCRUM-545 - W-9 Status Display
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  FileText,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  ArrowLeft,
  Download,
  ShieldCheck
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import StatusBadge from '../../components/common/StatusBadge';

export default function TaxDocuments() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [documents, setDocuments] = useState([]);
  const [w9Status, setW9Status] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTaxData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [docsResponse, w9Response] = await Promise.allSettled([
        apiRequest('/api/v1/guides/tax-documents'),
        apiRequest('/api/v1/guides/tax-documents/w9-status')
      ]);

      if (docsResponse.status === 'fulfilled' && docsResponse.value.success) {
        setDocuments(docsResponse.value.data || []);
      }

      if (w9Response.status === 'fulfilled' && w9Response.value.success) {
        setW9Status(w9Response.value.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load tax information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaxData();
  }, [fetchTaxData]);

  return (
    <DashboardLayout userRole="guide" activeNavId="earnings" onNavigate={handleNavigate}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/guide/earnings')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Earnings
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Tax Documents</h1>
          <p className="text-gray-600 mt-1">
            View your tax forms and W-9 status. Documents are generated by Stripe.
          </p>
        </div>

        {loading && (
          <LoadingSpinner message="Loading tax information..." />
        )}

        {error && (
          <Alert variant="error" message={error} className="mb-6" />
        )}

        {!loading && !error && (
          <>
            {/* W-9 Status */}
            {w9Status && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ShieldCheck className="w-6 h-6 text-emerald-600 mr-3" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">W-9 Tax Information</h2>
                      <p className="text-sm text-gray-600">
                        Required for 1099 form generation
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {w9Status.onFile ? (
                      <StatusBadge variant="success">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        On File
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="warning">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Not Submitted
                      </StatusBadge>
                    )}
                  </div>
                </div>
                {!w9Status.onFile && (
                  <p className="mt-3 text-sm text-amber-700">
                    Complete your tax information in Stripe to receive 1099 forms.
                  </p>
                )}
              </div>
            )}

            {/* Tax Documents List */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Available Documents</h2>
              </div>

              {documents.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No tax documents available yet</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Tax documents (1099-K, 1099-NEC) will appear here once generated by Stripe at year-end.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {documents.map((doc, index) => (
                    <div key={doc.id || index} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {doc.type || doc.formType || 'Tax Document'} - {doc.year || doc.taxYear}
                          </p>
                          {doc.status && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Status: {doc.status}
                            </p>
                          )}
                        </div>
                      </div>
                      {doc.downloadUrl && (
                        <a
                          href={doc.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-700"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stripe Dashboard Link */}
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Need more details?</p>
                  <p className="text-sm text-gray-600">
                    Visit your Stripe Express dashboard for complete tax management.
                  </p>
                </div>
                <a
                  href="https://connect.stripe.com/express_login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                >
                  Stripe Dashboard
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
